import type {
  BasicTeammate,
  CompanyTeammateListEntry,
  Teammate,
  TeammateId,
  TeammatePatchPayload,
  TeammatePerformanceDefaults,
} from "@repo/app-types/entities/teammate";
import type { RootState } from "../../app/store.js";
import {
  selectTeammate,
  setTeammate,
} from "../../features/teammate/teammateSlice.js";
import {
  buildListTag,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

interface UpdateTeammateRequest {
  id: TeammateId;
  patchPayload: TeammatePatchPayload;
}

const teammateApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allTeammates: builder.query<
      Teammate[],
      { includeDeactivated?: boolean } | void,
      { teammates: Teammate[] }
    >({
      query: (args) => ({
        method: "GET",
        url: "/teammates/",
        params: args?.includeDeactivated
          ? { include_deactivated: "true" }
          : undefined,
      }),
      transformResponse: (response) => response.teammates,
    }),

    basicTeammate: builder.query<
      BasicTeammate,
      TeammateId,
      { data: { teammate: BasicTeammate } }
    >({
      query: (id) => ({
        method: "GET",
        url: `/teammates/${encodeURIComponent(id)}`,
      }),
      transformResponse: (response) => response.data.teammate,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.BasicTeammate, id),
    }),

    teammate: builder.query<
      Teammate,
      TeammateId,
      { data: { teammate: Teammate } }
    >({
      query: (id) => ({
        method: "GET",
        url: `/teammates/${encodeURIComponent(id)}`,
      }),
      transformResponse: (response) => response.data.teammate,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.Teammate, id),
      // Warm the basicTeammate cache from the same response so consumers using
      // useBasicTeammateQuery don't refetch the same /teammates/:id endpoint.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertBasicTeammateQueryData(data));
        } catch {
          // The original query already surfaces the error to its caller.
        }
      },
    }),

    myPerformanceDefaults: builder.query<
      TeammatePerformanceDefaults,
      void,
      { data: { defaults: TeammatePerformanceDefaults } }
    >({
      query: () => ({
        method: "GET",
        url: "/teammates/me/performance-defaults",
      }),
      transformResponse: (response) => response.data.defaults,
    }),

    updateTeammate: builder.mutation<
      Teammate,
      UpdateTeammateRequest,
      { data: { teammate: Teammate } }
    >({
      query: ({ id, patchPayload }) => ({
        method: "PATCH",
        url: `/teammates/${encodeURIComponent(id)}`,
        body: patchPayload,
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: (_result, _error, { id, patchPayload }) => [
        ...getTagsInvalidatedByUpdate(TagType.BasicTeammate, id),
        // The update can move the teammate in or out of a filtered listing —
        // deactivating them takes the row out of the default roster — so the
        // list tag goes along with the entity tag.
        ...getTagsInvalidatedByUpdate(TagType.Teammate, id, {
          inLists: [{ filteredBy: [] }],
        }),
        ...(patchPayload.is_active !== undefined ||
        patchPayload.license_type !== undefined
          ? [buildListTag(TagType.LicenseUtilization, [])]
          : []),
        // Group memberships only list active teammates, so (de)activation
        // changes every group the teammate belongs to.
        ...(patchPayload.is_active !== undefined
          ? [buildListTag(TagType.Group, [])]
          : []),
      ],
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        const currentTeammate = selectTeammate(getState() as RootState);
        if (currentTeammate?.id !== id) {
          return;
        }

        try {
          const { data } = await queryFulfilled;
          dispatch(setTeammate(data));
        } catch {
          // Error handling is done by the caller
        }
      },
    }),

    updateMyProfile: builder.mutation<
      Teammate,
      { first_name: string; last_name: string },
      { data: { teammate: Teammate } }
    >({
      query: (body) => ({
        method: "PATCH",
        url: "/teammates/me",
        body,
      }),
      transformResponse: (response) => response.data.teammate,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setTeammate(data));
        } catch {
          // Error handling is done by the caller
        }
      },
    }),

    teammatesPaginated: builder.infiniteQuery<
      {
        teammates: CompanyTeammateListEntry[];
        nextCursor: string | null;
      },
      { search?: string; includeDeactivated?: boolean },
      string | null,
      {
        data: { teammates: CompanyTeammateListEntry[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "GET",
        url: "/teammates/paginated",
        params: {
          cursor: pageParam ?? undefined,
          limit: 20,
          search: queryArg.search ?? undefined,
          include_deactivated: queryArg.includeDeactivated ? "true" : undefined,
        },
      }),
      transformResponse: (response) => ({
        teammates: response.data.teammates,
        nextCursor: response.next_cursor,
      }),
      // `search` and `includeDeactivated` are part of the cache key, so every
      // combination is its own entry. They all provide the same list tag so a
      // mutation invalidates the entries the user isn't looking at too.
      providesTags: (result) =>
        getTagsProvidedByList(
          TagType.Teammate,
          result?.pages.flatMap((page) => page.teammates),
        ),
      // A roster entry is the same full teammate the entity endpoint returns
      // (both map through `mapTeammateDBToTeammate`), so warm its cache: a row
      // that renders somebody by id — their manager, say — then costs no
      // request for anybody already on a loaded page, and resolves them the
      // same way whether or not they are.
      //
      // One action for the whole roster rather than a dispatch per teammate:
      // this runs again on every `fetchNextPage` with the pages accumulated so
      // far, so per-teammate dispatches would grow with the square of the pages
      // scrolled. `has_pending_invitation` is dropped so the cached entry is
      // exactly what the entity endpoint would have returned.
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            teammateApi.util.upsertQueryEntries(
              data.pages.flatMap((page) =>
                page.teammates.map(
                  ({ has_pending_invitation: _, ...teammate }) => ({
                    endpointName: "teammate" as const,
                    arg: teammate.id,
                    value: teammate,
                  }),
                ),
              ),
            ),
          );
        } catch {
          // The original query already surfaces the error to its caller.
        }
      },
    }),
  }),
});

export const {
  useAllTeammatesQuery,
  useBasicTeammateQuery,
  useTeammateQuery,
  useMyPerformanceDefaultsQuery,
  useUpdateTeammateMutation,
  useUpdateMyProfileMutation,
  useTeammatesPaginatedInfiniteQuery,
} = teammateApi;

export function upsertBasicTeammateQueryData(teammate: BasicTeammate) {
  return teammateApi.util.upsertQueryData(
    "basicTeammate",
    teammate.id,
    teammate,
  );
}
